import React, { useState } from 'react'
import moment from 'moment-timezone'
import cookies from '../../utils/cookies'
import { useIsSecurityUser } from '../../hooks/useIsSecurityUser'
import useDebounce from '../../hooks/useDebounce'
import { getTypeLabel, isFaceReco } from '../../utils/events'
import useEventsStore from '../../stores/Events'
import CustomTable from '../customTable/CustomTable'
import ShowImageTable from '../common/ShowImageTable'
import ListHeader from '../common/ListHeader'
import { generatePDF } from './EventsReport'
import { Button } from '@material-ui/core'
import { isFromOperator } from '../../utils/events'
import Modal from '@material-ui/core/Modal'
import useStyles from '../common/FormFields/FormFieldsStyle'
import EventReportForm from './EventReportForm'
const eventTypes = [
  'OPEN_DOOR',
  'OPEN_DOOR_PIN',
  'OPEN_DOOR_INVITED',
  'MANUAL_ENTRY',
  'MANUAL_EXIT',
  'SOS',
  'GATES',
]

const FriendlyJSONDisplay = ({ jsonString, isSecurityUser }) => {
  if (!jsonString) return <div></div>
  // Parse the JSON string into a JavaScript object
  const jsonObject = JSON.parse(jsonString)

  // Style for the container div
  const containerStyle = {
    borderRadius: '5px',
    padding: '5px',
    maxWidth: '300px',
  }

  // Style for each key-value pair
  const keyValueStyle = {
    marginBottom: '5px',
    fontSize: '14px',
  }

  // Style for keys
  const keyStyle = {
    fontSize: '14px',
    marginRight: '5px',
    textTransform: 'capitalize',
  }
  const translateKey = (key) => {
    if (key === 'doorBell') {
      return 'Acceso'
    }
    if (key === 'doorBellType') {
      return 'Tipo Portero'
    }
    return key
  }
  const skipKeys = ['Nombre', 'CI']
  return (
    <div style={containerStyle}>
      <ul style={{ paddingLeft: '20px' }}>
        {Object.keys(jsonObject)
          .filter((key) => !(key === 'doorBellType' && isSecurityUser))
          .filter((key) => !skipKeys.includes(key))
          .map(
            (key) =>
              jsonObject[key] && (
                <li key={key} style={keyValueStyle}>
                  <span style={keyStyle}>{translateKey(key)}:</span>{' '}
                  {key === 'Acompañantes'
                    ? displayPayload(jsonObject[key])
                    : JSON.stringify(jsonObject[key]).replace(/"/g, '')}
                </li>
              )
          )
          .filter((item) => !!item)}
      </ul>
    </div>
  )
}

const displayPayload = (payload) => {
  return payload.map(
    (companion) => `${companion.firstName} ${companion.lastName}`
  )
}

export default function EventsList() {
  const eventsStore = useEventsStore((state) => state)
  const [modalOpen, setModalOpen] = useState(false)
  const classes = useStyles()

  const getEvents = useDebounce((params) => {
    eventsStore.getEvents(params)
  }, 1000)

  const loggedUser = JSON.parse(cookies.get('user'))
  const isSecurityUser = useIsSecurityUser()

  const [selectedBuilding, setSelectedBuilding] = useState(loggedUser?.building)
  const [params, setParams] = useState({})
  const apiRowsPerPage = 100

  const columns = [
    {
      id: 'image',
      label: 'Nombre',
      minWidth: 250,
      format: (event) => (
        <ShowImageTable
          imageUrl={event?.user?.selfie}
          name={`${event?.user?.name} ${event?.user?.surname}`}
          ci={event?.user?.ci || event.payload?.CI}
        ></ShowImageTable>
      ),
    },
    {
      id: 'type',
      label: 'Evento',
      minWidth: 170,
      format: (event) =>
        getTypeLabel(event.type, event.payload, event.doorbell),
    },
    {
      id: 'user.apartments',
      label: isSecurityUser ? 'Unidad' : 'Cliente/Unidad',
      minWidth: 170,
      format: (event) =>
        isSecurityUser
          ? event?.apartment?.name
          : event.user && event?.building?.name
          ? `${event?.building?.name} / ${event?.apartment?.name}`
          : '-',
    },
    {
      id: 'payload',
      label: 'Informacion extra',
      minWidth: 100,
      format: (event) => (
        <FriendlyJSONDisplay
          jsonString={JSON.stringify(event.payload)}
          isSecurityUser={isSecurityUser}
        ></FriendlyJSONDisplay>
      ),
    },
    {
      id: 'createdAt',
      label: 'Fecha',
      minWidth: 170,
      format: (event) =>
        moment
          .tz(event.createdAt, 'America/Argentina/Buenos_Aires')
          .format('DD/MM/YYYY HH:mm:ss')
          .toLocaleString(),
    },
    {
      id: 'createdBy',
      label: 'Concedido por',
      minWidth: 170,
      format: (event) =>
        (isFaceReco(event.type)
          ? 'Sistema'
          : isFromOperator(event?.type) &&
            `${event?.createdBy.name} ${event?.createdBy.surname}`) || '-',
    },
  ]

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    bgcolor: 'white',
    p: 4,
    overflow: 'hidden',
  }

  return (
    <>
      <ListHeader
        bigTitle="Historial de eventos"
        canCreate={true}
        createText="Exportar"
        createRedirect={() => setModalOpen(true)}
        noIcon
      />
      <CustomTable
        loading={eventsStore.loading}
        columns={columns}
        data={eventsStore.events || []}
        getData={(params) => {
          getEvents({
            ...params,
            dontShowDoorBellEvents: true,
            offset: 0,
            limit: apiRowsPerPage,
            cleanData: true,
          })
        }}
        onPageChange={(params, page) => {
          if ((page + 5) * 10 > eventsStore.events.length) {
            getEvents({
              ...params,
              dontShowDoorBellEvents: true,
              offset: page * 10,
              limit: apiRowsPerPage,
            })
          }
        }}
        buildingFilter={!selectedBuilding}
        building={selectedBuilding}
        apartmentFilter={true}
        searchFilter={'Acceso, CI, Empresa, etc'}
        options={false}
        serverPagination={false}
        serverCount={eventsStore.events.length}
        searchTitle={'Nombre, apellido, documento o matricula'}
        searchPlaceholder={'Ingrese un nombre, documento o matricula'}
        customFilters={[
          {
            field: 'type',
            label: 'Tipo de evento',
            type: 'select',
            width: '460px',
            options: eventTypes.map((type) => ({
              value: type,
              label: getTypeLabel(type, null, null),
            })),
            withRightMargin: true,
          },
          {
            field: 'untilDate',
            label: 'Eventos hasta la fecha',
            type: 'date',
            width: '260px',
          },
        ]}
        onParamsChange={(params) => setParams(params)}
      ></CustomTable>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="export-events"
      >
        <div style={style}>
          <EventReportForm
            closeModal={() => setModalOpen(false)}
            handleCancel={() => setModalOpen(false)}
            building={selectedBuilding}
            onSubmit={(params) => {
              eventsStore.exportEvents(
                {
                  buildingId: selectedBuilding?.id,
                  ...params,
                  limit: 3000,
                  offset: 0,
                  dontShowDoorBellEvents: true,
                },
                generatePDF
              )
            }}
          />
        </div>
      </Modal>
    </>
  )
}
